import { Node, mergeAttributes } from "@tiptap/core";

export type MediaEmbedKind = "youtube" | "vimeo" | "video" | "applePodcast";
export type MediaEmbedVariant = "episode" | "show";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        mediaEmbed: {
            insertMediaEmbed: (args: {
                kind: MediaEmbedKind;
                url: string;
                variant?: MediaEmbedVariant;
            }) => ReturnType;
        };
    }
}

const KIND_LABELS: Record<MediaEmbedKind, string> = {
    youtube: "YouTube",
    vimeo: "Vimeo",
    video: "Video",
    applePodcast: "Apple Podcasts",
};

// `kind` round-trips both the new canonical value (`applePodcast`) and the
// legacy hyphenated form (`apple-podcast`) used by the markdown directive
// pipeline. Read-side normalizes to the canonical form.
function normalizeKind(raw: string | null | undefined): MediaEmbedKind {
    if (raw === "apple-podcast") return "applePodcast";
    if (raw === "youtube" || raw === "vimeo" || raw === "video" || raw === "applePodcast") {
        return raw;
    }
    return "youtube";
}

export const MediaEmbedNode = Node.create({
    name: "mediaEmbed",
    group: "block",
    atom: true,
    selectable: true,
    draggable: false,
    isolating: true,

    addAttributes() {
        return {
            kind: {
                default: "youtube",
                parseHTML: (el) =>
                    normalizeKind(
                        (el as HTMLElement).getAttribute("data-media-embed-kind") ??
                            (el as HTMLElement).getAttribute("data-blog-embed-kind"),
                    ),
                renderHTML: (attrs) => ({ "data-media-embed-kind": attrs.kind }),
            },
            url: {
                default: "",
                parseHTML: (el) =>
                    (el as HTMLElement).getAttribute("data-media-embed-url") ??
                    (el as HTMLElement).getAttribute("data-blog-embed-url") ??
                    "",
                renderHTML: (attrs) => ({ "data-media-embed-url": attrs.url }),
            },
            variant: {
                default: null,
                parseHTML: (el) =>
                    (el as HTMLElement).getAttribute("data-media-embed-variant") ||
                    (el as HTMLElement).getAttribute("data-blog-embed-variant") ||
                    null,
                renderHTML: (attrs) =>
                    attrs.variant ? { "data-media-embed-variant": attrs.variant } : {},
            },
        };
    },

    parseHTML() {
        return [
            { tag: "div[data-media-embed]" },
            // Compat: legacy markdown pipeline emitted data-blog-embed.
            { tag: "div[data-blog-embed]" },
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        const kind = node.attrs.kind as MediaEmbedKind;
        const url = node.attrs.url as string;
        const variant = node.attrs.variant as MediaEmbedVariant | null;
        const label = variant
            ? `${KIND_LABELS[kind]} (${variant}) embed`
            : `${KIND_LABELS[kind]} embed`;
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-media-embed": "",
                contenteditable: "false",
                class: "media-embed-placeholder",
                style:
                    "display:block; margin:0.75rem 0; padding:0.75rem 1rem; " +
                    "border:1px dashed #94a3b8; background:#f8fafc; border-radius:0.5rem; " +
                    "font-size:0.8125rem; color:#475569;",
            }),
            `▶ ${label}: ${url || "(no URL set)"}`,
        ];
    },

    addCommands() {
        return {
            insertMediaEmbed:
                ({ kind, url, variant }) =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs: { kind, url, variant: variant ?? null },
                    }),
        };
    },
});
